import { useCallback, useMemo } from 'react';

import useModal from '~/hooks/useModal';
import { selectCurrentOrganizationId } from '~/modules/organization/organization.selectors';
import { selectSingleProjectId } from '~/modules/project/project.selectors';
import { useAppSelector } from '~/modules/store';
import { isApiError, isPaymentRequiredError } from '~/services/api/utils';
import toasts from '~/services/toasts';
import { ACTIONS } from '~/services/toasts/internalServerError';

import { testsApi } from '../../tests.api';
import { showTestsError } from '../../tests.utils';

export const useRunTests = () => {
  const modal = useModal();
  const organizationId = useAppSelector(selectCurrentOrganizationId);
  const projectId = useAppSelector(selectSingleProjectId);
  const [runTests, runTestsState] = testsApi.useRunTestsMutation();

  const handleRunTests = useCallback(
    async (params: Parameters<typeof runTests>[0]) => {
      const result = runTests({
        ...params,
        projectId: params.projectId || projectId,
      }).unwrap();

      result
        .then((testRuns) => {
          toasts.tests.showTestsStarted(testRuns, organizationId);
        })
        .catch((error) => {
          if (isApiError(error) && isPaymentRequiredError(error)) {
            modal.show('upgrade_plan');
            return;
          }

          showTestsError(ACTIONS.START, error as Error);
        });

      return result;
    },
    [runTests, projectId, organizationId, modal],
  );

  return useMemo(() => [handleRunTests, runTestsState] as const, [handleRunTests, runTestsState]);
};
